import { z } from 'zod';
import { t, tServer } from '@/shared/i18n';
import { setOnline } from '@/shared/offline/network';

/** Ошибка API в формате RFC 9457 (problem+json). */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly title: string,
    readonly violations: readonly { field: string; message: string }[] = [],
    /** Тело ответа целиком: по `code` интерфейс узнаёт причину (`dlp_warn` — показать плашку «Защиты данных»). */
    readonly body: unknown = null,
  ) {
    super(title);
  }

  /** Постоянный код причины (`two_factor_required`, `reauth_required`…), если сервер его прислал. */
  get code(): string | null {
    return readString(this.body, 'code') ?? null;
  }

  fieldError(field: string): string | undefined {
    const message = this.violations.find((v) => v.field === field)?.message;
    return message === undefined ? undefined : t(message);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

let csrfToken: string | null = null;

/** Сбрасываем после входа/выхода: сессия меняется, токен тоже. */
export function resetCsrf(): void {
  csrfToken = null;
}

async function getCsrf(): Promise<string> {
  if (csrfToken === null) {
    const response = await fetch('/api/auth/csrf', { credentials: 'same-origin' });
    const body: unknown = await response.json();
    csrfToken = readString(body, 'token') ?? '';
  }
  return csrfToken;
}

function readString(value: unknown, key: string): string | undefined {
  if (typeof value === 'object' && value !== null && key in value) {
    const field: unknown = Reflect.get(value, key);
    return typeof field === 'string' ? field : undefined;
  }
  return undefined;
}

function readViolations(value: unknown): { field: string; message: string }[] {
  if (typeof value !== 'object' || value === null || !('violations' in value)) {
    return [];
  }
  const list: unknown = Reflect.get(value, 'violations');
  if (!Array.isArray(list)) {
    return [];
  }
  return list.flatMap((item: unknown) => {
    const field = readString(item, 'field');
    const message = readString(item, 'message');
    return field !== undefined && message !== undefined ? [{ field, message }] : [];
  });
}

export interface RequestOptions {
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Дойти до сервера, даже если вкладку закрывают (последние правки документа). */
  readonly keepalive?: boolean;
}

/** fetch, который запоминает, дошёл ли запрос до сервера: от этого зависят знак «Нет сети» и очередь отправки. */
async function reach(url: string, init: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    setOnline(true);
    return response;
  } catch (error) {
    if (error instanceof TypeError) setOnline(false);
    throw error;
  }
}

async function send(method: Method, url: string, options: RequestOptions, retried = false): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = options.body instanceof FormData;
  const isBlob = options.body instanceof Blob;
  if (isBlob) {
    headers['Content-Type'] = 'application/octet-stream';
  } else if (options.body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET') {
    headers['X-CSRF-Token'] = await getCsrf();
  }
  const init: RequestInit = { method, headers, credentials: 'same-origin' };
  if (options.body instanceof FormData || options.body instanceof Blob) {
    // Файл: multipart браузер выставит сам с границей, кусок файла уходит как есть.
    init.body = options.body;
  } else if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }
  if (options.keepalive === true) {
    init.keepalive = true;
  }
  const response = await reach(url, init);
  if (response.status === 403 && method !== 'GET' && !retried) {
    // Токен мог устареть (новая сессия) — одна повторная попытка со свежим токеном.
    const clone = response.clone();
    const body: unknown = await clone.json().catch(() => null);
    if (readString(body, 'title') === 'Сессия устарела, обновите страницу') {
      resetCsrf();
      return send(method, url, options, true);
    }
  }
  return response;
}

/**
 * Ошибка по ответу сервера. Тело ошибки — JSON с заголовком от приложения или страница не от него
 * (nginx отклонил запрос, слишком большой файл, сервер перезапускается): тогда понятный текст по коду ответа,
 * а не «неожиданный ответ».
 */
function failure(status: number, text: string): ApiError {
  const body = jsonOrNull(text);
  return new ApiError(status, readString(body, 'title') ?? statusTitle(status), readViolations(body), body);
}

function jsonOrNull(text: string): unknown {
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function statusTitle(status: number): string {
  if (status === 413) return t('Файл слишком большой: сервер его не принял');
  if (status === 502 || status === 503 || status === 504) return t('Сервер сейчас перезапускается. Попробуйте через минуту');
  if (status === 403) return t('Сервер отклонил запрос. Если это повторяется — сообщите администратору');
  return t('Ошибка запроса');
}

export async function request<T>(method: Method, url: string, schema: z.ZodType<T>, options: RequestOptions = {}): Promise<T> {
  const response = await send(method, url, options);
  const text = await response.text();
  if (!response.ok) throw failure(response.status, text);
  const body: unknown = text === '' ? null : JSON.parse(text);
  return schema.parse(body);
}

export async function requestVoid(method: Method, url: string, options: RequestOptions = {}): Promise<void> {
  const response = await send(method, url, options);
  if (!response.ok) throw failure(response.status, await response.text());
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Заголовки ошибок сервер присылает по-русски; переводы — в словаре server.
    return tServer(error.title);
  }
  if (error instanceof z.ZodError || error instanceof SyntaxError) {
    // Сервер ответил, но не тем, что ждёт страница: так проще отличить ошибку программы от сбоя сети.
    console.error(error);
    return t('Сервер вернул неожиданный ответ. Обновите страницу; если не поможет — сообщите разработчику.');
  }
  return t('Не удалось связаться с сервером. Проверьте соединение и попробуйте ещё раз.');
}

/**
 * Ответ потоком (Server-Sent Events, строки `data: {…}`): каждое событие — в onEvent по мере прихода.
 * Ошибка до начала потока (нет прав, пустое сообщение) — исключением, как у request.
 */
export async function stream(url: string, body: unknown, onEvent: (event: unknown) => void, signal?: AbortSignal): Promise<void> {
  const response = await send('POST', url, signal === undefined ? { body } : { body, signal });
  if (!response.ok) throw failure(response.status, await response.text());
  const reader = response.body?.getReader();
  if (reader === undefined) return;
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
    let cut = buffer.indexOf('\n\n');
    while (cut >= 0) {
      const chunk = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const data = chunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (data !== '') onEvent(jsonOrNull(data));
      cut = buffer.indexOf('\n\n');
    }
    if (done) return;
  }
}
